import { useFormik } from 'formik'
import owasp, { TestResult } from 'owasp-password-strength-test'
import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import validator from 'validator'
import * as Yup from 'yup'
import { CreateUser, User } from '@citruscamps/citrus-client'
import { LoadingButton } from '../../../../../components/LoadingButton/LoadingButton'
import { PhoneInput } from '../../../../../components/PhoneInput/PhoneInput'
import { Title } from '../../../../../components/Title/Title'
import { ROUTES } from '../../../../../constants/routes'
import { useRouter } from '../../../../../hooks/useRouter'
import translations from '../../../../../translations/en.json'

interface IProps {
  initialValues?: User
  onSubmit: (user: CreateUser) => Promise<void>
}

export const AccountStep = ({ initialValues, onSubmit }: IProps): React.ReactElement => {
  const { location } = useRouter()
  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setValues,
    setFieldError,
    isSubmitting,
  } = useFormik<CreateUser>({
    initialValues: {
      first_name: initialValues?.first_name || '',
      last_name: initialValues?.last_name || '',
      email: initialValues?.email || '',
      phone: initialValues?.phone || '',
      password: '',
      timezone: initialValues?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    validationSchema: Yup.object().shape({
      first_name: Yup.string()
        .required((translations as any)['error.first_name_required'])
        .max(40, (translations as any)['error.first_name_maxlength']),
      last_name: Yup.string()
        .required((translations as any)['error.last_name_required'])
        .max(40, (translations as any)['error.last_name_maxlength']),
      phone: Yup.string()
        .required((translations as any)['error.phone_required'])
        .test(
          'phone',
          (translations as any)['error.phone_invalid'],
          (val) =>
            !val ||
            validator.isMobilePhone(`+${val.replace(/\D/g, '')}`, ['en-US', 'en-CA'], {
              strictMode: true,
            }),
        ),
      email: Yup.string()
        .required((translations as any)['error.email_required'])
        .test('email', (translations as any)['error.email_invalid'], (val) =>
          validator.isEmail(val || ''),
        ),
      timezone: Yup.string().required((translations as any)['error.default_inputs_message']),
      password: Yup.string()
        .test('required', (translations as any)['error.password_required'], function (val) {
          return !!initialValues?.id || !!val
        })
        .test('password', '', function (val) {
          const passwordStrength: TestResult = owasp.test(val || '')
          return (
            !!initialValues?.id ||
            (val &&
            !passwordStrength?.strong &&
            passwordStrength?.errors &&
            passwordStrength?.errors?.length > 0
              ? this.createError({
                  message: passwordStrength.errors[0] || '',
                  path: 'password',
                })
              : true)
          )
        }),
    }),
    onSubmit: async (value, { setSubmitting }) => {
      try {
        await onSubmit(value)
      } catch (e: any) {
        if (e.param) {
          setFieldError(e.param, e.message)
        } else if (e.message) {
          setFieldError('email', e.message)
        }
      }
      setSubmitting(false)
    },
  })

  useEffect(() => {
    if (initialValues?.id) {
      setValues({
        first_name: initialValues?.first_name || '',
        last_name: initialValues?.last_name || '',
        email: initialValues?.email || '',
        phone: initialValues?.phone || '',
        password: '',
        timezone: initialValues?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    }
  }, [initialValues, setValues])

  return (
    <form onSubmit={handleSubmit}>
      <Title value="Create Account" />
      <h4 className="mb-4">Tell us about yourself</h4>
      <div className="mb-3">
        <label className="form-label">Your first name</label>
        <input
          id="first_name"
          name="first_name"
          autoComplete="given-name"
          type="text"
          className={
            'form-control' + (touched.first_name && !!errors.first_name ? ' is-invalid' : '')
          }
          placeholder="Enter your first name"
          value={values.first_name}
          onBlur={handleBlur}
          onChange={handleChange}
          autoFocus
        />
        <div className="invalid-feedback d-block">
          {touched?.first_name && !!errors?.first_name ? errors?.first_name : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label">Your last name</label>
        <input
          id="last_name"
          name="last_name"
          autoComplete="family-name"
          type="text"
          className={
            'form-control' + (touched.last_name && !!errors.last_name ? ' is-invalid' : '')
          }
          placeholder="Enter your last name"
          value={values.last_name}
          onBlur={handleBlur}
          onChange={handleChange}
        />
        <div className="invalid-feedback d-block">
          {touched?.last_name && !!errors?.last_name ? errors?.last_name : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label">Your phone number</label>
        <PhoneInput
          id="phone"
          name="phone"
          className={'form-control' + (touched?.phone && errors?.phone ? ' is-invalid' : '')}
          value={values.phone}
          onBlur={handleBlur}
          onChange={(value: string, data: any, event: React.ChangeEvent) => handleChange(event)}
          onEnterKeyPress={() => {
            handleSubmit()
          }}
        />
        <div className="invalid-feedback d-block">
          {touched?.phone && errors?.phone ? errors?.phone : '\u00A0'}
        </div>
      </div>
      <div className="mb-3">
        <label className="form-label">Your email address</label>
        <input
          id="email"
          name="email"
          autoComplete="email"
          type="email"
          className={'form-control' + (touched.email && !!errors.email ? ' is-invalid' : '')}
          placeholder="Enter your email address"
          value={values.email}
          onBlur={handleBlur}
          onChange={handleChange}
        />
        <div className="invalid-feedback d-block">
          {touched?.email && !!errors?.email ? (
            errors?.email === 'Email is already taken.' ? (
              <>
                Email is already associated with a Citrus account.{' '}
                <Link
                  to={{
                    pathname: ROUTES.LOGIN,
                    search: `?goto=${encodeURI(ROUTES.SIGNUP + location.search)}`,
                  }}
                  className="ms-1"
                >
                  Click here to login
                </Link>
              </>
            ) : (
              errors?.email
            )
          ) : (
            '\u00A0'
          )}
        </div>
      </div>
      {!initialValues?.id && (
        <div className="mb-3">
          <label className="form-label">Your password</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            className={
              'form-control' + (touched.password && !!errors.password ? ' is-invalid' : '')
            }
            placeholder="Enter your password"
            value={values.password}
            onBlur={handleBlur}
            onChange={handleChange}
          />
          <div className="invalid-feedback d-block">
            {touched?.password && !!errors?.password ? errors?.password : '\u00A0'}
          </div>
        </div>
      )}
      <div className="mb-3 form-text small text-body-secondary">
        By clicking the "Next" button you agree to our{' '}
        <a href="https://www.joincitrus.com/privacy-policy">Privacy Policy</a> and{' '}
        <a href="https://www.joincitrus.com/terms-of-service">Terms and Conditions of Use</a>.
      </div>
      <div className="d-flex justify-content-between mt-4">
        <a
          href="https://www.joincitrus.com"
          className={`btn btn-link me-auto${isSubmitting ? ' disabled' : ''}`}
        >
          Cancel
        </a>
        <LoadingButton type="submit" className="btn btn-primary ms-auto" loading={isSubmitting}>
          Next
        </LoadingButton>
      </div>
    </form>
  )
}
